/**
 * Reusable add/edit coding profile form (MUI Dialog).
 * Opens from “+ Add Profile” or an edit action; controlled local state; validates and trims username on submit.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';

import { CODING_PROFILE_TYPE_OPTIONS } from '../utils/codingProfiles';

/** @typedef {'leetcode' | 'github' | 'codeforces' | 'codechef'} CodingProfileType */

export { CODING_PROFILE_TYPE_OPTIONS, CODING_PROFILE_TYPE_TO_FIELD } from '../utils/codingProfiles';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'add' | 'edit'} props.mode
 * @param {string} [props.initialProfileType]
 * @param {string} [props.initialUsername]
 * @param {string[]} [props.occupiedProfileTypes] — in `add` mode, types that already have a profile (hidden from select)
 * @param {(payload: { profileType: CodingProfileType, username: string, mode: 'add' | 'edit' }) => void} props.onSubmit — `username` is trimmed
 */
const CodingProfileFormDialog = ({
  open,
  onClose,
  mode = 'add',
  initialProfileType = '',
  initialUsername = '',
  occupiedProfileTypes = [],
  onSubmit,
}) => {
  const [profileType, setProfileType] = useState('');
  const [username, setUsername] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProfileType(initialProfileType || '');
    setUsername(initialUsername ?? '');
    setSubmitAttempted(false);
  }, [open, initialProfileType, initialUsername]);

  const trimmedUsername = username.trim();
  const addModeAvailableTypes = CODING_PROFILE_TYPE_OPTIONS.filter(
    (opt) => !occupiedProfileTypes.includes(opt.value),
  );
  const noAddSlotsLeft = mode === 'add' && addModeAvailableTypes.length === 0;

  const profileTypeInvalid = submitAttempted && !profileType && !noAddSlotsLeft;
  const usernameInvalid = submitAttempted && !trimmedUsername;

  const handleClose = () => {
    setSubmitAttempted(false);
    onClose();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (noAddSlotsLeft) return;
    if (!profileType || !trimmedUsername) return;
    if (mode === 'add' && occupiedProfileTypes.includes(profileType)) return;
    onSubmit?.({ profileType, username: trimmedUsername, mode });
    handleClose();
  };

  const title = mode === 'edit' ? 'Edit coding profile' : 'Add coding profile';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="coding-profile-dialog-title">
      <DialogTitle id="coding-profile-dialog-title">{title}</DialogTitle>
      <Box component="form" onSubmit={handleFormSubmit} noValidate>
        <DialogContent sx={{ pt: 1 }}>
          {noAddSlotsLeft ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              All profile types are already linked. Edit or remove one in the table to add a different platform.
            </Alert>
          ) : null}
          <TextField
            select
            required
            fullWidth
            label="Profile Type"
            name="profileType"
            value={profileType}
            onChange={(e) => setProfileType(e.target.value)}
            disabled={mode === 'edit' || noAddSlotsLeft}
            error={profileTypeInvalid}
            helperText={profileTypeInvalid ? 'Profile type is required' : ''}
          >
            <MenuItem value="">
              <em>Select a platform</em>
            </MenuItem>
            {(mode === 'edit' ? CODING_PROFILE_TYPE_OPTIONS : addModeAvailableTypes).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            fullWidth
            label="Username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g., your_handle"
            error={usernameInvalid}
            helperText={
              usernameInvalid ? 'Username is required' : 'Whitespace is trimmed when you save'
            }
            margin="normal"
            disabled={noAddSlotsLeft}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={handleClose} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={noAddSlotsLeft}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {mode === 'edit' ? 'Save Changes' : 'Add Profile'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CodingProfileFormDialog;
